import { ONBOARDING_AGENT_KINDS, type AgentKind } from "../../agents";

/* A grid of selectable agent-type tiles. Presentational — the selected kind is
 * owned by the caller. Styled from the onboarding tile tokens (--onb-tile-*):
 * the selected tile is the crimson tint behind a crimson hairline in both
 * themes — the light card's values live in ai21-brand.css, the drawer's maroon
 * ground remaps them in app-theme.css. Only the tile kinds appear: a hand-
 * configured kind is not a peer of the one-click ones, and reaches its guide
 * from the link under the step's CTA instead.
 *
 * `value` is null before the reader has answered: creation deliberately
 * pre-selects nothing, so the step asks the question rather than assuming it.
 * Every tile is its own tab stop, which is what keeps the grid reachable in
 * that state — there is no checked tile to carry a single one. */
export function AgentTypePicker({
  value,
  onChange,
  disabled,
}: {
  /** The picked kind, or null while the reader has not picked one. */
  value: AgentKind | null;
  onChange: (kind: AgentKind) => void;
  disabled?: boolean;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Agent type"
      className="grid grid-cols-2 gap-2"
    >
      {ONBOARDING_AGENT_KINDS.map(({ value: kind, label, Icon }) => {
        const selected = kind === value;
        return (
          <button
            key={kind}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            onClick={() => onChange(kind)}
            className="flex items-center gap-2.5 rounded-[5px] px-4 py-3 text-left transition-colors hover:bg-[var(--onb-hover)] disabled:cursor-not-allowed disabled:opacity-60"
            style={{
              border: `1px solid ${
                selected
                  ? "var(--onb-tile-active-border)"
                  : "var(--onb-tile-border)"
              }`,
              // Background only set when selected so the hover utility can
              // paint the unselected tiles (inline style would beat it).
              background: selected ? "var(--onb-tile-active-bg)" : undefined,
              color: selected ? "var(--onb-tile-active-fg)" : "var(--fg-1)",
            }}
          >
            <span
              className="shrink-0 [&_svg]:size-[18px]"
              style={{
                color: selected ? "var(--onb-tile-active-fg)" : "var(--fg-3)",
              }}
            >
              <Icon />
            </span>
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "14px",
                fontWeight: 500,
                lineHeight: "20px",
              }}
            >
              {label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
